import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { getPublicSiteOrigin } from '../constants/publicUrl';

export const BUSINESS_ANNUAL_FEE_GNF = 500000;
export const ACTIVITY_PUBLISH_FEE_GNF = 50000;

/** Plans d’affichage entreprise (alignés sur functions/listingFees.js). */
export const BUSINESS_FEE_PLANS = {
  month: {
    id: 'month',
    months: 1,
    amountGnf: 50000,
    labelFr: 'Mensuel',
    labelEn: 'Monthly'
  },
  quarter: {
    id: 'quarter',
    months: 3,
    amountGnf: 125000,
    labelFr: 'Trimestriel',
    labelEn: 'Quarterly'
  },
  semester: {
    id: 'semester',
    months: 6,
    amountGnf: 250000,
    labelFr: 'Semestriel',
    labelEn: 'Semi-annual'
  },
  year: {
    id: 'year',
    months: 12,
    amountGnf: BUSINESS_ANNUAL_FEE_GNF,
    labelFr: 'Annuel',
    labelEn: 'Annual'
  }
};

export function getBusinessFeePlan(period = 'year') {
  return BUSINESS_FEE_PLANS[period] || BUSINESS_FEE_PLANS.year;
}

const payBusinessAnnualFeeFn = httpsCallable(functions, 'payBusinessAnnualFee');
const createBusinessListingInvoiceFn = httpsCallable(functions, 'createBusinessListingInvoice');
const submitActivityWithFeeFn = httpsCallable(functions, 'submitActivityWithFee');
const approveFeePaymentProofFn = httpsCallable(functions, 'approveFeePaymentProof');
const initiateBusinessListingFeeMoMoFn = httpsCallable(functions, 'initiateBusinessListingFeeMoMo');
const checkBusinessListingFeeMoMoStatusFn = httpsCallable(functions, 'checkBusinessListingFeeMoMoStatus');
const confirmDemoBusinessListingFeeMoMoFn = httpsCallable(functions, 'confirmDemoBusinessListingFeeMoMo');
const initiateFeeMoMoPaymentFn = httpsCallable(functions, 'initiateFeeMoMoPayment');
const checkFeeMoMoStatusFn = httpsCallable(functions, 'checkFeeMoMoStatus');
const confirmDemoFeeMoMoFn = httpsCallable(functions, 'confirmDemoFeeMoMo');

export async function payBusinessAnnualFee({
  businessId,
  billingPeriod = 'year',
  paymentMethod = 'wallet',
  receiptUrl = null,
  payerReference = null,
  agentCode = null
} = {}) {
  try {
    const { data } = await payBusinessAnnualFeeFn({
      businessId,
      billingPeriod,
      paymentMethod,
      receiptUrl,
      payerReference,
      agentCode: agentCode || undefined
    });
    return data;
  } catch (error) {
    const code = String(error?.code || '');
    const msg = String(error?.message || '');
    if (code.includes('internal') || /^INTERNAL$/i.test(msg.trim())) {
      const err = new Error(
        'Le serveur n’a pas pu finaliser le paiement des frais. Réessayez, ou payez par Mobile Money avec reçu.'
      );
      err.code = error.code;
      err.cause = error;
      throw err;
    }
    throw error;
  }
}

export async function initiateBusinessListingFeeMoMo({
  businessId,
  billingPeriod = 'year',
  method = 'mtn_money',
  phone,
  agentCode = null
} = {}) {
  const { data } = await initiateBusinessListingFeeMoMoFn({
    businessId,
    billingPeriod,
    method,
    phone,
    agentCode: agentCode || undefined,
    returnOrigin: getPublicSiteOrigin()
  });
  return data;
}

export async function checkBusinessListingFeeMoMoStatus(paymentId) {
  const { data } = await checkBusinessListingFeeMoMoStatusFn({ paymentId });
  return data;
}

export async function confirmDemoBusinessListingFeeMoMo(paymentId) {
  const { data } = await confirmDemoBusinessListingFeeMoMoFn({ paymentId });
  return data;
}

/** MoMo Collection générique (activités, crédits, location, …). */
export async function initiateFeeMoMoPayment(payload = {}) {
  const { data } = await initiateFeeMoMoPaymentFn({
    ...payload,
    returnOrigin: getPublicSiteOrigin()
  });
  return data;
}

export async function checkFeeMoMoStatus(paymentId) {
  const { data } = await checkFeeMoMoStatusFn({ paymentId });
  return data;
}

export async function confirmDemoFeeMoMo(paymentId) {
  const { data } = await confirmDemoFeeMoMoFn({ paymentId });
  return data;
}

export async function waitForFeeMoMoResult(
  paymentId,
  { intervalMs = 3000, maxAttempts = 40, onPending } = {}
) {
  for (let i = 0; i < maxAttempts; i += 1) {
    const status = await checkFeeMoMoStatus(paymentId);
    if (typeof onPending === 'function') onPending(status);
    if (['success', 'failed', 'cancelled'].includes(status.status)) {
      return status;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return { paymentId, status: 'pending', timedOut: true };
}

export async function waitForListingFeeMoMoResult(
  paymentId,
  { intervalMs = 3000, maxAttempts = 40, onPending } = {}
) {
  for (let i = 0; i < maxAttempts; i += 1) {
    const status = await checkBusinessListingFeeMoMoStatus(paymentId);
    if (typeof onPending === 'function') onPending(status);
    if (['success', 'failed', 'cancelled'].includes(status.status)) {
      return status;
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return { paymentId, status: 'pending', timedOut: true };
}

export async function createBusinessListingInvoice({
  businessId,
  billingPeriod = 'year'
} = {}) {
  const { data } = await createBusinessListingInvoiceFn({
    businessId,
    billingPeriod
  });
  return data;
}

export async function submitActivityWithFee(payload = {}) {
  const { data } = await submitActivityWithFeeFn(payload);
  return data;
}

export async function approveFeePaymentProof({ proofId, action = 'approve' } = {}) {
  const { data } = await approveFeePaymentProofFn({ proofId, action });
  return data;
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Photo / logo utilisable pour l’affichage public. */
export function hasBusinessPhoto(business) {
  if (!business) return false;
  const firstOf = (list) => {
    if (!Array.isArray(list) || !list.length) return '';
    const item = list[0];
    if (typeof item === 'string') return item;
    return item?.url || item?.src || item?.downloadURL || '';
  };
  const candidates = [
    business.logo,
    business.photo,
    business.image,
    business.coverImage,
    firstOf(business.photos),
    firstOf(business.images)
  ];
  return candidates.some((raw) => {
    const url = String(raw || '').trim();
    if (!url || url === 'null' || url === 'undefined') return false;
    return /^(https?:\/\/|data:image\/|\/)/i.test(url) || url.includes('firebasestorage');
  });
}

/** Visible grand public : approuvée, active, photo, et frais à jour s’ils existent. */
export function isBusinessPubliclyVisible(business) {
  if (!business) return false;
  const statusOk = business.status === 'approved' || business.status === 'validated';
  if (!statusOk) return false;
  if (business.isActive === false) return false;
  if (!hasBusinessPhoto(business)) return false;
  if (business.listingFeeExempt === true) return true;
  const exp = toDate(business.listingFeeExpiresAt);
  if (!exp) return true;
  if (exp.getTime() <= Date.now()) return false;
  return true;
}

export function getBusinessVisibilityIssue(business) {
  if (!business) return 'missing';
  const statusOk = business.status === 'approved' || business.status === 'validated';
  if (!statusOk) return 'not_approved';
  if (business.isActive === false) return 'inactive';
  if (!hasBusinessPhoto(business)) return 'no_photo';
  if (business.listingFeeExempt === true) return null;
  const exp = toDate(business.listingFeeExpiresAt);
  if (exp && exp.getTime() <= Date.now()) return 'fee';
  return null;
}

export function formatListingExpiry(date, locale = 'fr-FR') {
  if (!date) return '—';
  return toDate(date).toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

/**
 * Statut cotisation listing pour l’admin.
 * @returns {{ key: string, labelFr: string, labelEn: string, expiresAt: Date|null, daysLeft: number|null }}
 */
export function getBusinessListingFeeStatus(business = {}) {
  if (business.listingFeeExempt === true) {
    return {
      key: 'exempt',
      labelFr: 'Exonérée',
      labelEn: 'Exempt',
      expiresAt: null,
      daysLeft: null
    };
  }

  if (String(business.listingFeeStatus || '') === 'pending_review') {
    const exp = toDate(business.listingFeeExpiresAt);
    return {
      key: 'pending_review',
      labelFr: 'Reçu en attente',
      labelEn: 'Receipt pending',
      expiresAt: exp,
      daysLeft: exp ? Math.ceil((exp.getTime() - Date.now()) / 86400000) : null
    };
  }

  const exp = toDate(business.listingFeeExpiresAt);
  if (!exp) {
    return {
      key: 'unset',
      labelFr: 'Sans échéance',
      labelEn: 'No expiry set',
      expiresAt: null,
      daysLeft: null
    };
  }

  const daysLeft = Math.ceil((exp.getTime() - Date.now()) / 86400000);
  if (daysLeft <= 0) {
    return {
      key: 'overdue',
      labelFr: 'Pas à jour',
      labelEn: 'Overdue',
      expiresAt: exp,
      daysLeft
    };
  }
  if (daysLeft <= 7) {
    return {
      key: 'soon',
      labelFr: 'Expire bientôt',
      labelEn: 'Expiring soon',
      expiresAt: exp,
      daysLeft
    };
  }
  return {
    key: 'current',
    labelFr: 'À jour',
    labelEn: 'Up to date',
    expiresAt: exp,
    daysLeft
  };
}

/** Bucket admin : à jour vs pas à jour (soon = alerte, pas encore « pas à jour »). */
export function isListingFeeUpToDate(business) {
  const status = getBusinessListingFeeStatus(business).key;
  return status === 'current' || status === 'exempt' || status === 'unset' || status === 'soon';
}

export function isListingFeeBehind(business) {
  const status = getBusinessListingFeeStatus(business).key;
  return status === 'overdue' || status === 'pending_review';
}
